/**
 * Dashboard Page - Main overview dashboard
 *
 * Shows:
 * - Key performance metrics (Cash Collected, Revenue, Close Rate)
 * - Call statistics (Show Rate, Calls Due/Taken/Closed)
 * - Average metrics (Cash per Call, Cash per Close)
 * - Additional stats (New Cash, MRR, Total Gross Revenue)
 * - Charts (Closes by Country, Lead Source, Content Performance)
 * - Recent calls table
 *
 * Renders via the DashboardShell widget system.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MetricCardSkeleton, StatCardSkeleton, FilterBar } from "../components";
import { AdditionalStatCardSkeleton } from "../components/StatCard";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useSession } from "../auth/client";
import { DashboardDataProvider } from "../context/DashboardContext";
import { DashboardShell } from "../components/dashboard/DashboardShell";
import { DASHBOARD_DEFAULT } from "../components/dashboard/defaults";
import type { DashboardData } from "../components/dashboard/types";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { EditRowDialog, type FieldDefinition } from "../components/EditRowDialog";

interface DashboardMetrics {
  totalCashCollected: number;
  totalRevenue: number;
  closeRate: number;
  showRate: number;
  callsDue: number;
  callsTaken: number;
  callsClosed: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  avgCashPerApplication: number;
  avgCashPerOptin: number;
  totalApplicationsCash: number;
  totalOptinsCash: number;
  newCashCollected: number;
  mrrCash: number;
  totalGrossRevenue: number;
  totalApplications: number;
  totalOptins: number;
  cancelledCalls: number;
  cancelRate: number;
}

interface ChartDataItem {
  name: string;
  value: number;
  percentage: number;
  revenue?: number;
}

interface TrendPoint {
  date: string;
  value: number;
}

interface RecentCall {
  id: string;
  _id: string;
  timestamp: string;
  dateCallWasTaken: string;
  leadName: string;
  email: string;
  phone: string;
  closer: string;
  setter: string;
  status: "closed" | "no-show" | "pending" | "not-interested";
  callOutcome: string;
  cashCollected: number;
  notes: string;
  leadSourcePlatform?: string;
  leadSourceContent?: string;
}

// Field definitions for editing calls
const CALL_EDIT_FIELDS: FieldDefinition[] = [
  { key: "leadName", label: "Lead Name", type: "text", required: true },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "closer", label: "Closer", type: "text" },
  { key: "setter", label: "Setter", type: "text" },
  { key: "callOutcome", label: "Call Outcome", type: "text" },
  { key: "notes", label: "Notes", type: "textarea" },
];

interface FilterOption {
  value: string;
  label: string;
}

interface ContentOption extends FilterOption {
  allValues?: string[]; // Multiple video IDs that map to the same title
}

interface FilterOptions {
  closers: FilterOption[];
  setters: FilterOption[];
  funnels: FilterOption[]; // Now has value/label for normalized display
  contents: ContentOption[];
  countries: string[];
  sources: FilterOption[];
  offers: FilterOption[];
}

function optionMatchesValue(
  options: Array<FilterOption | ContentOption | string>,
  value: string,
): boolean {
  const valueParts = String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return options.some((option) => {
    if (typeof option === "string") return option === value;
    const optionParts = String(option.value || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    const extraParts =
      "allValues" in option && Array.isArray(option.allValues) ? option.allValues : [];
    return (
      option.value === value ||
      option.label === value ||
      optionParts.includes(value) ||
      extraParts.includes(value) ||
      (valueParts.length > 0 && valueParts.every((part) => optionParts.includes(part) || extraParts.includes(part)))
    );
  });
}

function resolveGroupedOptionValue(
  options: Array<FilterOption | ContentOption | string>,
  rawValue: string,
): string {
  const rawParts = String(rawValue || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const match = options.find((option) => {
    if (typeof option === "string") return option === rawValue;
    const optionParts = String(option.value || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    const extraParts =
      "allValues" in option && Array.isArray(option.allValues) ? option.allValues : [];
    return (
      option.value === rawValue ||
      option.label === rawValue ||
      optionParts.includes(rawValue) ||
      extraParts.includes(rawValue) ||
      (rawParts.length > 0 && rawParts.every((part) => optionParts.includes(part) || extraParts.includes(part)))
    );
  });

  if (!match || typeof match === "string") return rawValue;
  return match.value || rawValue;
}

function selectedValueContains(selectedValue: string, rawValue: string): boolean {
  if (!selectedValue || !rawValue) return false;
  const selectedParts = selectedValue.split(",").map((value) => value.trim()).filter(Boolean);
  const rawParts = rawValue.split(",").map((value) => value.trim()).filter(Boolean);
  return rawParts.some((part) => selectedParts.includes(part));
}

// Helper to normalize filter options that may be string[] or FilterOption[]
function normalizeToFilterOptions(data: unknown): FilterOption[] {
  if (!Array.isArray(data)) return [];
  return data.map((item: unknown) => {
    if (typeof item === "string") {
      return { value: item, label: item };
    }
    if (item && typeof item === "object" && "value" in item) {
      const obj = item as { value: string; label?: string };
      return { value: obj.value, label: obj.label || obj.value };
    }
    return { value: String(item), label: String(item) };
  });
}

export function Dashboard() {
  // Get current user session
  const { data: session } = useSession();

  // Use global date range from context (synced with header date picker)
  const { dateRange: globalDateRange, formatForApi } = useDateRange();

  // Use shared filters from context (synced with URL via nuqs)
  const {
    dateField,
    closers,
    setters,
    funnels,
    contents,
    countries,
    sources,
    leadType,
    callOutcome,
    offer,
    setDateField,
    setLeadType,
    setCallOutcome,
    recentCallSearch,
    recentCallStatus,
    setRecentCallSearch,
    setRecentCallStatus,
    // Array setters for clearing invalid filters
    setClosers,
    setSetters,
    setFunnels,
    setContents,
    setCountries,
    setSources,
    offers,
    setOffers,
    clearAllFilters,
  } = useFilters();

  // Data states
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [cashTrend, setCashTrend] = useState<TrendPoint[]>([]);
  const [trendType, setTrendType] = useState<"daily" | "monthly">("monthly");
  const [countryData, setCountryData] = useState<ChartDataItem[]>([]);
  const [leadSourceData, setLeadSourceData] = useState<ChartDataItem[]>([]);
  const [contentData, setContentData] = useState<ChartDataItem[]>([]);
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  // Server-side pagination state
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotalItems, setTableTotalItems] = useState(0);
  const [tableTotalPages, setTableTotalPages] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    closers: [],
    setters: [],
    funnels: [],
    contents: [],
    countries: [],
    sources: [],
    offers: [],
  });
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Chart filter mode: "closed" shows only closed leads, "all" shows all leads
  const [chartFilterMode, setChartFilterMode] = useState<"closed" | "all">("closed");

  const tableFilters = useMemo(
    () => ({
      recentCalls: {
        search: recentCallSearch,
        status: recentCallStatus,
        closer: closers.join(","),
        setter: setters.join(","),
        callOutcome,
      },
    }),
    [recentCallSearch, recentCallStatus, closers, setters, callOutcome]
  );

  // Edit/Delete state for recent calls
  const [editingCall, setEditingCall] = useState<RecentCall | null>(null);
  const [deletingCall, setDeletingCall] = useState<RecentCall | null>(null);

  // Handle edit
  const handleEditCall = (call: RecentCall) => {
    setEditingCall(call);
  };

  // Handle save
  const handleSaveCall = async (data: Record<string, unknown>) => {
    // Map frontend field names to backend field names
    const mappedData = {
      prospectName: data.leadName,
      prospectEmail: data.email,
      prospectNumber: data.phone,
      closerName: data.closer,
      setterName: data.setter,
      callOutcome: data.callOutcome,
      callNotes: data.notes,
    };

    const response = await fetch(`/api/leads/${data._id || data.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(mappedData),
    });

    if (!response.ok) {
      throw new Error("Failed to update call");
    }

    // Refresh data
    fetchData(false);
  };

  // Handle delete
  const handleDeleteCall = (call: RecentCall) => {
    setDeletingCall(call);
  };

  // Confirm delete
  const confirmDeleteCall = async () => {
    if (!deletingCall) return;

    const response = await fetch(`/api/leads/${deletingCall._id || deletingCall.id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Failed to delete call");
    }

    // Refresh data
    fetchData(false);
  };

  // Fetch data from API
  // tableOnly: if true, only show table loading (for pagination changes)
  const fetchData = useCallback(async (showLoading = true, tableOnly = false) => {
    if (showLoading) {
      if (tableOnly) {
        setTableLoading(true);
      } else {
        setIsLoading(true);
      }
    }
    try {
      // Use global date range from context
      const apiDates = formatForApi();
      const params = new URLSearchParams();
      params.set("startDate", apiDates.startDate);
      params.set("endDate", apiDates.endDate);
      if (dateField) params.set("dateField", dateField);
      if (closers.length > 0) params.set("closer", closers.join(","));
      if (setters.length > 0) params.set("setter", setters.join(","));
      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      if (leadType) params.set("leadType", leadType);
      if (contents.length > 0) params.set("content", contents.join(","));
      if (countries.length > 0) params.set("country", countries.join(","));
      if (sources.length > 0) params.set("source", sources.join(","));
      if (offer && offer !== "all") params.set("offer", offer);
      const selectedOffers = offers.filter((o: string) => o !== "all");
      if (offer === "all" && selectedOffers.length > 0) params.set("offer", selectedOffers.join(","));
      // Chart filter: closedOnly=true shows only closed leads in charts
      params.set("closedOnly", chartFilterMode === "closed" ? "true" : "false");
      // Pagination params
      params.set("page", String(tablePage));
      params.set("pageSize", String(tablePageSize));
      const recentCallsFilters = tableFilters.recentCalls || {};
      if (recentCallsFilters.search) params.set("search", recentCallsFilters.search);
      if (recentCallsFilters.status) params.set("status", recentCallsFilters.status);
      if (recentCallsFilters.closer) params.set("tableCloser", recentCallsFilters.closer);
      if (recentCallsFilters.setter) params.set("tableSetter", recentCallsFilters.setter);
      if (recentCallsFilters.callOutcome) params.set("tableCallOutcome", recentCallsFilters.callOutcome);

      const response = await fetch(`/api/dashboard/summary?${params}`);
      const result = await response.json();

      if (!result.success) throw new Error(result.error);

      const { data } = result;

      setMetrics({
        totalCashCollected: data.metrics.totalCashCollected || 0,
        totalRevenue: data.metrics.totalRevenue || 0,
        closeRate: data.metrics.closeRate || 0,
        showRate: data.metrics.showRate || 0,
        callsDue: data.metrics.callsDue || 0,
        callsTaken: data.metrics.callsTaken || 0,
        callsClosed: data.metrics.callsClosed || 0,
        avgCashPerCall: data.metrics.avgCashPerCall || 0,
        avgCashPerClose: data.metrics.avgCashPerClose || 0,
        avgCashPerApplication: data.metrics.avgCashPerApplication || 0,
        avgCashPerOptin: data.metrics.avgCashPerOptin || 0,
        totalApplicationsCash: data.metrics.totalApplicationsCash || 0,
        totalOptinsCash: data.metrics.totalOptinsCash || 0,
        newCashCollected: data.metrics.newCashCollected || 0,
        mrrCash: data.metrics.mrrCash || 0,
        totalGrossRevenue: data.metrics.totalGrossRevenue || 0,
        totalApplications: data.metrics.totalApplications || 0,
        totalOptins: data.metrics.totalOptins || 0,
        cancelledCalls: data.metrics.cancelledCalls || 0,
        cancelRate: data.metrics.cancelRate || 0,
      });

      if (data.filterOptions) {
        setFilterOptions({
          closers: normalizeToFilterOptions(data.filterOptions.closers),
          setters: normalizeToFilterOptions(data.filterOptions.setters),
          funnels: normalizeToFilterOptions(data.filterOptions.funnels),
          contents: (data.filterOptions.contents || []).map((c: ContentOption | string) =>
            typeof c === "string" ? { value: c, label: c } : c
          ),
          countries: data.filterOptions.countries || [],
          sources: normalizeToFilterOptions(data.filterOptions.sources),
          offers: normalizeToFilterOptions(data.filterOptions.offers),
        });
      }

      setCashTrend(data.cashTrend || []);
      setTrendType(data.trendType || "monthly");
      setCountryData(data.countryData || []);
      setLeadSourceData(data.leadSourceData || []);
      setContentData(data.contentData || []);
      setRecentCalls(data.recentCalls || []);

      // Update pagination state from server response
      if (data.pagination) {
        setTableTotalItems(data.pagination.totalItems);
        setTableTotalPages(data.pagination.totalPages);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch dashboard metrics:", error);
    } finally {
      if (showLoading) {
        if (tableOnly) {
          setTableLoading(false);
        } else {
          setIsLoading(false);
        }
      }
    }
  }, [formatForApi, dateField, closers, setters, funnels, leadType, contents, countries, sources, offer, offers, chartFilterMode, tablePage, tablePageSize, tableFilters]);

  // Auto-remove invalid filters when available options change
  useEffect(() => {
    // Skip if filterOptions haven't loaded yet
    if (!filterOptions.closers.length && !filterOptions.setters.length && !filterOptions.funnels.length) {
      return;
    }

    const filtersToRemove: Record<string, string[]> = {};

    // Check closer filter - if selected closer not in available options, remove it
    if (closers.length > 0 && filterOptions.closers.length > 0) {
      const validClosers = closers.filter((selectedCloser) =>
        filterOptions.closers.some((option) =>
          option.value === selectedCloser ||
          option.label === selectedCloser ||
          selectedValueContains(option.value, selectedCloser) ||
          selectedValueContains(selectedCloser, option.value)
        )
      );
      if (validClosers.length !== closers.length) {
        filtersToRemove.closers = validClosers;
      }
    }

    // Check setter filter
    if (setters.length > 0 && filterOptions.setters.length > 0) {
      const validSetters = setters.filter((selectedSetter) =>
        filterOptions.setters.some((option) =>
          option.value === selectedSetter ||
          option.label === selectedSetter ||
          selectedValueContains(option.value, selectedSetter) ||
          selectedValueContains(selectedSetter, option.value)
        )
      );
      if (validSetters.length !== setters.length) {
        filtersToRemove.setters = validSetters;
      }
    }

    // Check funnel filter (uses value/label format)
    if (funnels.length > 0 && filterOptions.funnels.length > 0) {
      const validFunnels = funnels.filter((selectedFunnel) =>
        filterOptions.funnels.some((option) => optionMatchesValue([option], selectedFunnel))
      );
      if (validFunnels.length !== funnels.length) {
        filtersToRemove.funnels = validFunnels;
      }
    }

    // Check content filter (uses value/label format)
    if (contents.length > 0 && filterOptions.contents.length > 0) {
      const validContents = contents.filter((selectedContent) => optionMatchesValue(filterOptions.contents, selectedContent));
      if (validContents.length !== contents.length) {
        filtersToRemove.contents = validContents;
      }
    }

    // Check country filter
    if (countries.length > 0 && filterOptions.countries.length > 0) {
      const validCountries = countries.filter((selectedCountry) => filterOptions.countries.includes(selectedCountry));
      if (validCountries.length !== countries.length) {
        filtersToRemove.countries = validCountries;
      }
    }

    // Check source filter
    if (sources.length > 0 && filterOptions.sources.length > 0) {
      const validSources = sources.filter((selectedSource) =>
        filterOptions.sources.some((option) =>
          option.value === selectedSource ||
          option.label === selectedSource ||
          selectedValueContains(option.value, selectedSource) ||
          selectedValueContains(selectedSource, option.value)
        )
      );
      if (validSources.length !== sources.length) {
        filtersToRemove.sources = validSources;
      }
    }

    // Remove invalid filters
    if (Object.keys(filtersToRemove).length > 0) {
      if ('closers' in filtersToRemove) setClosers(filtersToRemove.closers ?? []);
      if ('setters' in filtersToRemove) setSetters(filtersToRemove.setters ?? []);
      if ('funnels' in filtersToRemove) setFunnels(filtersToRemove.funnels ?? []);
      if ('contents' in filtersToRemove) setContents(filtersToRemove.contents ?? []);
      if ('countries' in filtersToRemove) setCountries(filtersToRemove.countries ?? []);
      if ('sources' in filtersToRemove) setSources(filtersToRemove.sources ?? []);
    }
  }, [filterOptions, closers, setters, funnels, contents, countries, sources, setClosers, setSetters, setFunnels, setContents, setCountries, setSources]);

  // Track if the next fetch should be table-only (for pagination changes)
  const isPaginationChangeRef = useRef(false);

  // Reset to page 1 when filters change
  useEffect(() => {
    isPaginationChangeRef.current = false;
    setTablePage(1);
  }, [globalDateRange, dateField, closers, setters, funnels, leadType, contents, countries, sources, chartFilterMode, recentCallSearch, recentCallStatus, callOutcome]);

  // Handle pagination changes - mark as pagination-only before state update
  const handlePageChange = (page: number) => {
    isPaginationChangeRef.current = true;
    setTablePage(page);
  };

  const handlePageSizeChange = (size: number) => {
    isPaginationChangeRef.current = true;
    setTablePageSize(size);
    setTablePage(1);
  };

  const handleSetTableFilter = useCallback((tableKey: string, field: string, value: string) => {
    if (tableKey !== "recentCalls") return;
    isPaginationChangeRef.current = false;
    setTablePage(1);
    switch (field) {
      case "search":
        setRecentCallSearch(value);
        break;
      case "status":
        setRecentCallStatus(value);
        break;
      case "closer":
        setClosers(value ? [value] : []);
        break;
      case "setter":
        setSetters(value ? [value] : []);
        break;
      case "callOutcome":
        setCallOutcome(value);
        break;
      default:
        break;
    }
  }, [setRecentCallSearch, setRecentCallStatus, setClosers, setSetters, setCallOutcome]);

  const handleClearTableFilters = useCallback((tableKey: string) => {
    if (tableKey !== "recentCalls") return;
    isPaginationChangeRef.current = false;
    setTablePage(1);
    setRecentCallSearch("");
    setRecentCallStatus("");
    setClosers([]);
    setSetters([]);
    setCallOutcome("");
  }, [setRecentCallSearch, setRecentCallStatus, setClosers, setSetters, setCallOutcome]);

  // Fetch data when dependencies change
  useEffect(() => {
    const isTableOnly = isPaginationChangeRef.current;
    isPaginationChangeRef.current = false; // Reset for next change
    fetchData(true, isTableOnly);
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchData(false), 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Clear all filters (date range is managed globally by the header picker)
  const handleClearAll = () => {
    clearAllFilters();
  };

  // Get greeting based on time of day with user's first name
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = session?.user?.name?.split(" ")[0] || "";
    const timeGreeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  };

  // --- Map API data into DashboardData context format ---
  const dashboardData: DashboardData = useMemo(() => ({
    page: "dashboard",
    metrics: {
      totalCashCollected: metrics?.totalCashCollected ?? 0,
      totalRevenue: metrics?.totalRevenue ?? 0,
      closeRate: metrics?.closeRate ?? 0,
      showRate: metrics?.showRate ?? 0,
      callsDue: metrics?.callsDue ?? 0,
      callsTaken: metrics?.callsTaken ?? 0,
      callsClosed: metrics?.callsClosed ?? 0,
      avgCashPerCall: metrics?.avgCashPerCall ?? 0,
      avgCashPerClose: metrics?.avgCashPerClose ?? 0,
      avgCashPerApplication: metrics?.avgCashPerApplication ?? 0,
      avgCashPerOptin: metrics?.avgCashPerOptin ?? 0,
      totalApplicationsCash: metrics?.totalApplicationsCash ?? 0,
      totalOptinsCash: metrics?.totalOptinsCash ?? 0,
      newCashCollected: metrics?.newCashCollected ?? 0,
      mrrCash: metrics?.mrrCash ?? 0,
      totalGrossRevenue: metrics?.totalGrossRevenue ?? 0,
      totalApplications: metrics?.totalApplications ?? 0,
      totalOptins: metrics?.totalOptins ?? 0,
      cancelledCalls: metrics?.cancelledCalls ?? 0,
      cancelRate: metrics?.cancelRate ?? 0,
    },
    chartData: {
      countryData: countryData,
      leadSourceData: leadSourceData,
      contentData: contentData,
      cashTrend: cashTrend,
    },
    chartInteractions: {
      countryData: {
        onSelect: (value) => {
          if (value === "Other" || value === "Unknown" || value === "N/A") return;
          const nextValue = resolveGroupedOptionValue(filterOptions.countries, value);
          setCountries(
            selectedValueContains(countries.join(","), value)
              ? countries.filter((selectedCountry) => !selectedValueContains(selectedCountry, nextValue) && !selectedValueContains(nextValue, selectedCountry))
              : [...countries, nextValue]
          );
        },
        isActive: (value) => selectedValueContains(countries.join(","), value),
      },
      leadSourceData: {
        onSelect: (value) => {
          if (value === "Other" || value === "Unknown" || value === "N/A" || !value.trim()) return;
          const nextValue = resolveGroupedOptionValue(filterOptions.sources, value);
          setSources(
            selectedValueContains(sources.join(","), value)
              ? sources.filter((selectedSource) => !selectedValueContains(selectedSource, nextValue) && !selectedValueContains(nextValue, selectedSource))
              : [...sources, nextValue]
          );
        },
        isActive: (value) => selectedValueContains(sources.join(","), value),
      },
      contentData: {
        onSelect: (value) => {
          if (value === "Other" || value === "Unknown" || value === "N/A") return;
          const nextValue = resolveGroupedOptionValue(filterOptions.contents, value);
          setContents(
            selectedValueContains(contents.join(","), value)
              ? contents.filter((selectedContent) => !selectedValueContains(selectedContent, nextValue) && !selectedValueContains(nextValue, selectedContent))
              : [...contents, nextValue]
          );
        },
        isActive: (value) => selectedValueContains(contents.join(","), value),
      },
    },
    tableData: {
      recentCalls: recentCalls,
    },
    filterOptions: {
      closers: filterOptions.closers,
      setters: filterOptions.setters,
      funnels: filterOptions.funnels,
      contents: filterOptions.contents,
      countries: filterOptions.countries,
      sources: filterOptions.sources,
      offers: filterOptions.offers,
    },
    rawData: {
      trendType,
      chartFilterMode,
      setChartFilterMode,
      tableLoading,
      tableFilters,
      setTableFilter: handleSetTableFilter,
      clearTableFilters: handleClearTableFilters,
      pagination: {
        currentPage: tablePage,
        pageSize: tablePageSize,
        totalItems: tableTotalItems,
        totalPages: tableTotalPages,
      },
      handlePageChange,
      handlePageSizeChange,
      handleEditCall: handleEditCall,
      handleDeleteCall: handleDeleteCall,
    },
    isLoading,
    refetch: () => fetchData(false),
  }), [
    metrics, countryData, leadSourceData, contentData, cashTrend,
    recentCalls, filterOptions, trendType, chartFilterMode, tableLoading, tableFilters,
    tablePage, tablePageSize, tableTotalItems, tableTotalPages,
    isLoading, fetchData, handlePageChange, handlePageSizeChange,
    handleEditCall, handleDeleteCall, countries, sources, contents,
    handleSetTableFilter, handleClearTableFilters,
    setCountries, setSources, setContents,
  ]);

  // Only show skeleton on initial load, not when filtering (keep old data visible)
  if (isLoading && !metrics) {
    return (
      <div className="space-y-6">
        {/* Row 1: Metric Cards Skeleton */}
        <div className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <MetricCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Row 2: Call Statistics Skeleton */}
        <div className="grid gap-3 md:gap-4 grid-cols-2 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Row 3: Average Metrics Skeleton */}
        <div className="grid gap-3 md:gap-4 grid-cols-2 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <StatCardSkeleton key={i} index={i + 4} />
          ))}
        </div>

        {/* Row 4: Total Cash Breakdown Skeleton */}
        <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2">
          {[0, 1].map((i) => (
            <StatCardSkeleton key={i} index={i + 8} />
          ))}
        </div>

        {/* Row 5: Additional Statistics Skeleton */}
        <div className="grid gap-3 md:gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-6">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <AdditionalStatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Chart Skeletons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="relative overflow-hidden bg-card rounded-xl border border-border p-6"
        >
          <div className="h-5 w-40 rounded bg-muted animate-pulse mb-4" />
          <div className="h-[400px] rounded-lg bg-muted/30 animate-pulse flex items-center justify-center">
            <motion.div
              className="w-32 h-32 rounded-full border-4 border-muted border-t-muted-foreground/30"
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <motion.div
            className="absolute inset-0"
            animate={{ x: ["-100%", "100%"] }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            style={{
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)",
            }}
          />
        </motion.div>
      </div>
    );
  }

  // --- Header JSX ---
  const headerContent = (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="flex items-center justify-between gap-2 w-full"
    >
      <motion.h1
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="text-lg md:text-2xl font-bold tracking-tight truncate"
      >
        {getGreeting()}
      </motion.h1>

      <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
        <AnimatePresence mode="wait">
          {lastUpdated && (
            <motion.span
              key="updated"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="text-xs text-muted-foreground hidden sm:block"
            >
              Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </motion.span>
          )}
        </AnimatePresence>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setAutoRefresh(!autoRefresh)}
          className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors ${
            autoRefresh
              ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400"
              : "bg-muted text-muted-foreground"
          }`}
        >
          <motion.span
            animate={autoRefresh ? { scale: [1, 1.2, 1] } : {}}
            transition={{ duration: 1.5, repeat: Infinity }}
            className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${autoRefresh ? "bg-green-500" : "bg-gray-400"}`}
          />
          <span className="hidden xs:inline">{autoRefresh ? "Live" : "Paused"}</span>
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.1, rotate: 90 }}
          whileTap={{ scale: 0.9 }}
          transition={{ type: "spring", stiffness: 300 }}
          onClick={() => fetchData(false)}
          className="p-1.5 md:p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
          title="Refresh now"
        >
          <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </motion.button>
      </div>
    </motion.div>
  );

  // --- Filter Bar JSX ---
  const filterBarContent = (
    <FilterBar
      showDateFieldToggle={true}
      dateField={dateField as "timestamp" | "dateCallWasTaken"}
      onDateFieldChange={(v) => setDateField(v)}
      filters={[
        {
          key: "funnel",
          label: "All Funnels",
          options: filterOptions.funnels.map((f) => ({ value: f.value, label: f.label })),
          value: funnels,
          onChange: (v) => setFunnels(v as string[]),
          multiple: true,
        },
        {
          key: "leadType",
          label: "All Lead Types",
          options: [
            { value: "applicant", label: "Applicant" },
            { value: "optin", label: "Opt-in" },
          ],
          value: leadType,
          onChange: (v) => setLeadType(v as string),
        },
        {
          key: "content",
          label: "Select Content",
          options: filterOptions.contents.map((c) => ({ value: c.value, label: c.label.length > 30 ? c.label.slice(0, 30) + "..." : c.label })),
          value: contents,
          onChange: (v) => setContents(v as string[]),
          multiple: true,
        },
        {
          key: "closer",
          label: "Select Closers",
          options: filterOptions.closers,
          value: closers,
          onChange: (v) => setClosers(v as string[]),
          multiple: true,
        },
        {
          key: "setter",
          label: "Select Setters",
          options: filterOptions.setters,
          value: setters,
          onChange: (v) => setSetters(v as string[]),
          multiple: true,
        },
        ...(offer === "all" && filterOptions.offers.length > 0
          ? [{
              key: "offers",
              label: "Select Offers",
              options: filterOptions.offers,
              value: offers.filter((o: string) => o !== "all"),
              onChange: (v: string | string[]) => setOffers(["all", ...(v as string[])]),
              multiple: true,
            }]
          : []),
      ]}
      onClearAll={handleClearAll}
    />
  );

  return (
    <DashboardDataProvider value={dashboardData}>
      <DashboardShell
        page="dashboard"
        defaultLayout={DASHBOARD_DEFAULT}
        filterBar={filterBarContent}
        header={headerContent}
      >
        {/* Edit Dialog */}
        <EditRowDialog
          open={!!editingCall}
          onOpenChange={(open) => !open && setEditingCall(null)}
          title="Edit Call"
          description="Update call information"
          fields={CALL_EDIT_FIELDS}
          data={editingCall as Record<string, unknown> | null}
          onSave={handleSaveCall}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={!!deletingCall}
          onOpenChange={(open) => !open && setDeletingCall(null)}
          title="Delete Call"
          itemName={deletingCall?.leadName}
          onConfirm={confirmDeleteCall}
        />
      </DashboardShell>
    </DashboardDataProvider>
  );
}
